import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {PropertyType, ResourceType} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {BookmarkOverrides, DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {rison} from 'common/util/rison';
import {CohortOperatorListMap, ListOperator} from 'common/widgets/property-filter-menu/models/types';
import {relativeDatesToFullLengthDateRange} from 'common/util/date-converters';
import {ChartType, FunnelBookmark, TrendCountType, TrendUnit} from 'common/report/funnels/models/types';
import {fromEnum} from 'common/report/funnels/util';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';

import {paramsFromGroupBySelectedCohorts, paramsFromSegfilter} from './param-converters';

function passThrough(param, ...path) {
  return (source) => {
    const obj = path.reduce((node, attr) => node[attr], source);
    const value = obj?.[param];
    if (!isNil(value) && (!Array.isArray(value) || value.length > 0)) {
      return {[param]: value};
    }
    return null;
  };
}

const appStateFieldsFromURL = [`originDashboard`, `title`];

const queryFieldsFromURL: Array<Partial<keyof FunnelsQuery>> = [
  `chartType`,
  `cohortFilters`,
  `comparedDateRange`,
  `conversionWindow`,
  `countType`,
  `dataGroupId`,
  `dateRange`,
  `eventFilters`,
  `events`,
  `exclusionSteps`,
  `frequencyAggregateType`,
  `frequencyBuckets`,
  `frequencyVizType`,
  `funnelAttributionSelection`,
  `funnelOrder`,
  `groupBy`,
  `holdingPropertyConstant`,
  `selectedTableColumns`,
  `stepsVizType`,
  `timeBuckets`,
  `trendCountType`,
  `trendUnit`,
  `ttcAggregateType`,
  `ttcVizType`,
  `liftComparison`,
  `userFilters`,
  `eventIndex`,
  `savedLegend`,
  `selected`,
  `trendType`,
  `frequencyType`,
  `querySampling`,
  `frequencyValueRepresentation`,
  `ttcValueRepresentation`,
  `sortField`,
  `sortOrder`,
  `sorting`,
];

const BOOKMARK_PARAM_TO_URL_TRANSLATION = {
  selectedSegments: `selected`,
};

const URL_PARAMS = {
  chartType: {
    decode: ({chartType}) => {
      const chartTypeDecoded = fromEnum(chartType, ChartType, null);
      return chartTypeDecoded === null ? {} : {chartType: chartTypeDecoded};
    },
    encode: passThrough(`chartType`, `query`),
  },
  cohortFilter: {
    decode: ({cohortFilter}) => {
      if (isEmpty(cohortFilter)) {
        return null;
      }

      // TODO: deprecate this param when there are no more logged instances
      reporter.info(`cohortFilter param decoded.`, {cohortFilter});

      return {
        cohortFilters: [
          {
            filterOperator: cohortFilter.negated ? ListOperator.DoesNotContain : ListOperator.Contains,
            filterValue: [cohortFilter],
            propertyType: PropertyType.List,
            resourceType: ResourceType.Cohort,
          },
        ],
      };
    },
  },
  cohortFilters: {
    decode: ({cohortFilters}) => {
      if (isEmpty(cohortFilters)) {
        return null;
      }

      // TODO: deprecate this `in` operator conversion
      if (cohortFilters.some((filter) => CohortOperatorListMap[filter.filterOperator])) {
        reporter.info(`cohortFilter deprecated operator decoded.`, {cohortFilters});
      }

      return {
        cohortFilters: cohortFilters.map((filter) => ({
          ...filter,
          filterOperator: CohortOperatorListMap[filter.filterOperator] || filter.filterOperator,
          propertyType: PropertyType.List,
        })),
      };
    },
    encode: passThrough(`cohortFilters`, `query`),
  },
  savedLegend: {
    decode: passThrough(`savedLegend`),
    encode: passThrough(`savedLegend`, `query`),
  },
  selected: {
    decode: passThrough(`selected`),
    encode: passThrough(`selected`, `query`),
  },
  selectMode: {
    decode: passThrough(`selectMode`),
    encode: passThrough(`selectMode`, `query`),
  },
  trendType: {
    decode: passThrough(`trendType`),
    encode: passThrough(`trendType`, `query`),
  },
  conversionWindow: {
    decode: passThrough(`conversionWindow`),
    encode: passThrough(`conversionWindow`, `query`),
  },
  countType: {
    decode: passThrough(`countType`),
    encode: passThrough(`countType`, `query`),
  },
  dataGroupId: {
    decode: passThrough(`dataGroupId`),
    encode: passThrough(`dataGroupId`, `query`),
  },
  dateRange: {
    decode: passThrough(`dateRange`),
    encode: ({query}) => query?.dateRange && {dateRange: query.dateRange.getAttributes()},
  },
  comparedDateRange: {
    decode: passThrough(`comparedDateRange`),
    encode: passThrough(`comparedDateRange`, `query`),
  },
  eventFilters: {
    decode: passThrough(`eventFilters`),
    encode: passThrough(`eventFilters`, `query`),
  },
  eventIndex: {
    decode: passThrough(`eventIndex`),
    encode: passThrough(`eventIndex`, `query`),
  },
  events: {
    decode: passThrough(`events`),
    encode: passThrough(`events`, `query`),
  },
  exclusionSteps: {
    decode: passThrough(`exclusionSteps`),
    encode: passThrough(`exclusionSteps`, `query`),
  },
  frequencyAggregateType: {
    decode: passThrough(`frequencyAggregateType`),
    encode: passThrough(`frequencyAggregateType`, `query`),
  },
  ttcAggregateType: {
    decode: passThrough(`ttcAggregateType`),
    encode: passThrough(`ttcAggregateType`, `query`),
  },
  liftComparison: {
    decode: passThrough(`liftComparison`),
    encode: passThrough(`liftComparison`, `query`),
  },
  frequencyType: {
    decode: passThrough(`frequencyType`),
    encode: passThrough(`frequencyType`, `query`),
  },
  frequencyVizType: {
    decode: passThrough(`frequencyVizType`),
    encode: passThrough(`frequencyVizType`, `query`),
  },
  ttcVizType: {
    decode: passThrough(`ttcVizType`),
    encode: passThrough(`ttcVizType`, `query`),
  },
  stepsVizType: {
    decode: passThrough(`stepsVizType`),
    encode: passThrough(`stepsVizType`, `query`),
  },
  frequencyValueRepresentation: {
    decode: passThrough(`frequencyValueRepresentation`),
    encode: passThrough(`frequencyValueRepresentation`, `query`),
  },
  ttcValueRepresentation: {
    decode: passThrough(`ttcValueRepresentation`),
    encode: passThrough(`ttcValueRepresentation`, `query`),
  },
  groupBy: {
    decode: passThrough(`groupBy`),
    encode: passThrough(`groupBy`, `query`),
  },
  groupByOverride: {
    decode: ({groupByOverride}) => (groupByOverride ? {groupBy: groupByOverride} : {}),
  },
  erfTreatments: {
    decode: passThrough(`erfTreatments`), // to be merged with group bys in params or bookmark
  },
  holdingPropertyConstant: {
    decode: ({holdingPropertyConstant}) => {
      if (isEmpty(holdingPropertyConstant)) {
        return null;
      }

      if (holdingPropertyConstant.some((item) => item.value)) {
        // handle older urls
        return {
          holdingPropertyConstant: holdingPropertyConstant.map((item) => ({
            resourceType: ResourceType.Event,
            propertyType: PropertyType.String,
            propertyDefaultType: PropertyType.String,
            propertyName: item.value,
          })),
        };
      } else {
        return passThrough(`holdingPropertyConstant`)({holdingPropertyConstant});
      }
    },
    encode: passThrough(`holdingPropertyConstant`, `query`),
  },
  emailSource: {
    decode: ({emailSource}) =>
      emailSource && {
        odaParams: {
          task_id: emailSource.taskId,
          inbound_segment: emailSource.inboundSegment,
          oda_type: emailSource.odaType,
          oda_email_origin: true,
        },
      },
  },
  timeBuckets: {
    decode: passThrough(`timeBuckets`),
    encode: passThrough(`timeBuckets`, `query`),
  },
  frequencyBuckets: {
    decode: passThrough(`frequencyBuckets`),
    encode: passThrough(`frequencyBuckets`, `query`),
  },
  trendUnit: {
    decode: ({trendUnit}) => Object.values(TrendUnit).includes(trendUnit) && {trendUnit},
    encode: passThrough(`trendUnit`, `query`),
  },
  trendCountType: {
    decode: ({trendCountType}) => Object.values(TrendCountType).includes(trendCountType) && {trendCountType},
    encode: passThrough(`trendCountType`, `query`),
  },
  userFilters: {
    decode: passThrough(`userFilters`),
    encode: passThrough(`userFilters`, `query`),
  },
  incomingUrlVersion: {
    decode: ({incomingUrlVersion}) => ({urlVersion: incomingUrlVersion}),
  },
  funnelOrder: {
    decode: passThrough(`funnelOrder`),
    encode: passThrough(`funnelOrder`, `query`),
  },
  querySampling: {
    decode: passThrough(`querySampling`),
    encode: passThrough(`querySampling`, `query`),
  },
  funnelAttributionSelection: {
    decode: passThrough(`funnelAttributionSelection`),
    encode: passThrough(`funnelAttributionSelection`, `query`),
  },
  additionalFilters: {
    decode: passThrough(`additionalFilters`), // to be merged with global filters in params or bookmark
  },
  chartTypeOverride: {
    decode: passThrough(`chartTypeOverride`),
  },
  dateRangeOverride: {
    decode: passThrough(`dateRangeOverride`),
  },
  sortField: {
    decode: passThrough(`sortField`),
    encode: passThrough(`sortField`, `query`),
  },
  sortOrder: {
    decode: passThrough(`sortOrder`),
    encode: passThrough(`sortOrder`, `query`),
  },
  sorting: {
    decode: passThrough(`sorting`),
    encode: passThrough(`sorting`, `query`),
  },
  selectedTableColumns: {
    decode: passThrough(`selectedTableColumns`),
    encode: passThrough(`selectedTableColumns`, `query`),
  },
  originDashboard: {
    decode: ({originDashboard}) => {
      return originDashboard ? {originDashboard} : {};
    },
    encode: ({originDashboard}) => {
      return originDashboard ? {originDashboard} : {};
    },
  },
  title: {
    decode: passThrough(`title`),
    encode: passThrough(`title`),
  },
};

function v1ToV2(v1Params) {
  let v2Params = {};
  if (`conversion_unit` in v1Params) {
    v2Params[`trendUnit`] = v1Params[`conversion_unit`];
  }
  if (`event_index` in v1Params) {
    v2Params[`eventIndex`] = v1Params[`event_index`];
  }

  if (`segfilter` in v1Params) {
    v2Params = {...v2Params, ...paramsFromSegfilter(v1Params[`segfilter`])};
  } else if (`group_by_selected_cohorts` in v1Params) {
    v2Params = {...v2Params, ...paramsFromGroupBySelectedCohorts(v1Params[`group_by_selected_cohorts`])};
  }

  if (`from_date` in v1Params && `to_date` in v1Params) {
    v2Params[`dateRange`] = relativeDatesToFullLengthDateRange({
      relativeFrom: v1Params.from_date,
      relativeTo: v1Params.to_date,
    });
  }
  if (`email_source` in v1Params) {
    v2Params[`emailSource`] = {
      taskId: v1Params[`email_source`].task_id,
      inboundSegment: v1Params[`email_source`].inbound_segment,
      odaType: v1Params[`email_source`].oda_type,
    };
  }
  v2Params[`v`] = 2;
  return v2Params;
}

function updateParams(oldParams) {
  let newParams = {};
  if (!(`v` in oldParams) || oldParams[`v`] === 1) {
    newParams = updateParams(v1ToV2(oldParams));
  } else if (oldParams[`v`] === 2) {
    newParams = oldParams;
  } else {
    reporter.error(new Error(`Unrecognized URL version`), oldParams[`v`]);
  }
  newParams[`incomingUrlVersion`] = oldParams[`v`] || 0;
  return newParams;
}

export function decode(paramString) {
  let urlData = {};
  const params = rison.parse(`(${paramString || ``})`);
  if (!params) {
    return urlData;
  }

  const overrides: BookmarkOverrides = pick(params, BookmarkOverrides);
  const latestParams = {
    ...updateParams(params),
    ...overrides,
  };
  Object.keys(URL_PARAMS).forEach((param) => {
    const decoder = URL_PARAMS[param].decode;
    if (decoder) {
      urlData = {...urlData, ...decoder(latestParams)};
    }
  });
  return urlData;
}

export function encode(appState: {originDashboard?: DashboardOrigin; title?: string; query?: FunnelsQuery}, {version}) {
  let urlParams = {};
  Object.keys(URL_PARAMS).forEach((param) => {
    const encoder = URL_PARAMS[param].encode;
    if (encoder) {
      urlParams = {...urlParams, ...encoder(appState)};
    }
  });

  urlParams[`v`] = version;

  const encoded = rison.urlencode(urlParams);
  return encoded.substring(1, encoded.length - 1);
}

export function translateBookmarkParamsToUrlParams(bookmark: FunnelBookmark) {
  // Necessary for some funnel params which are encoded as a different keyword in the url than in the bookmark
  // This is temporary code which will be replaced soon with a non-url based solution - Bruna Feb 9th 2020
  const urlParams = {};
  const paramsToTranslate = pick(bookmark, Object.keys(BOOKMARK_PARAM_TO_URL_TRANSLATION));
  Object.keys(paramsToTranslate).forEach((param) => {
    const translateTo = BOOKMARK_PARAM_TO_URL_TRANSLATION[param];
    urlParams[translateTo] = paramsToTranslate[param];
  });
  return urlParams;
}

// after urlData is processed by decode, overrides could be changed into other names like the below. Filter these
// additional ones out if they show up since they may be overrides from dashboards. If we aren't explicitly overridden
// by dashboards but the bookmark has still changed, we'd get a bunch more of these so it's ok for omit
const processedBookmarkOverrides = [
  ...BookmarkOverrides,
  `chartType`,
  `dateRange`,
  `emailSource`,
  `groupBy`,
  `odaParams`,
  `trendUnit`,
  `urlVersion`,
  `originDashboard`,
];

export function urlHasUnsavedChanges(urlData) {
  return Object.keys(omit(urlData, processedBookmarkOverrides)).length > 0;
}

export function urlHasDashboardOverrides(urlData) {
  return Object.keys(pick(urlData, processedBookmarkOverrides)).length > 0;
}

// pick funnels' appState fields from decoded urlData
export function appStateFromURL(urlData) {
  return pick(urlData, appStateFieldsFromURL);
}

// pick funnels' query fields from decoded urlData
export function queryStateFromURL(urlData): Partial<FunnelsQuery> {
  return pick(urlData, queryFieldsFromURL);
}

// pick funnels' non-query fields from decoded urlData
export function nonQueryStateFromURL(urlData): TODO {
  return omit(urlData, queryFieldsFromURL);
}

export function getParamsFromUrl(
  funnelIdOrParams: string,
  reportNameOrParams: string | null,
  paramStringOrSlug: string | null,
): {funnelId: number; paramString: string | null} {
  // Determine which url parts we actually have, as different URLs we can have are:
  // 1. funnels#view/rison_string          -> (funnelIdOrParams = rison string)
  // 2. funnels#view/id                   -> (funnelIdOrParams = id)
  // 3. funnels#view/id/title             -> (funnelIdOrParams = id, reportNameOrParams = title)
  // 4. funnels#view/id/rison_string       -> (funnelIdOrParams = id, reportNameOrParams = rison string)
  // 5. funnels#view/id/title/rison_string -> (funnelIdOrParams = id, reportNameOrParams = title, paramStringOrSlug = rison string)

  let funnelId = null;
  let paramString = null;

  const isValid = (str: string): boolean => {
    try {
      // if we have a the first half of a query (legit query that was split somewhere in the middle on a /)
      // or arbitrary string, it will likely parse but be "empty"
      const decoded = decode(str);
      return urlHasDashboardOverrides(decoded);
    } catch {
      // if we have a string that begins with a closing paren we'll throw a call stack error
      return false;
    }
  };

  if (/^\d+$/.test(funnelIdOrParams)) {
    funnelId = parseInt(funnelIdOrParams, 10);

    // if we have two parts, and individually they are both "bad", but when we join them they're valid, join them
    //
    // eg.#view/102345/chartType:chart,conversionWindow:(...filterOperator:equals,filterValue:!(/),propertyDefaultType...
    // we would split on the slash which was actually a filter in the params
    if (
      reportNameOrParams &&
      paramStringOrSlug &&
      !isValid(reportNameOrParams) &&
      isValid(`${reportNameOrParams}/${paramStringOrSlug}`)
    ) {
      paramString = `${reportNameOrParams}/${paramStringOrSlug}`;
    } else if (paramStringOrSlug) {
      paramString = paramStringOrSlug;
    } else if (reportNameOrParams) {
      const decodedParams = decode(reportNameOrParams);
      if (urlHasDashboardOverrides(decodedParams)) {
        paramString = reportNameOrParams;
      }
    }
  } else {
    // we don't have an id, its all rison string and we may have incorrectly split before we got here
    // eg. #view/chartType:chart,conversionWindow:(...filterOperator:equals,filterValue:!(/),propertyDefaultType...
    paramString = [funnelIdOrParams, reportNameOrParams, paramStringOrSlug].filter((x) => x !== null).join(`/`);
  }
  return {funnelId, paramString};
}
